import { useState, type FormEvent, type KeyboardEvent, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import { legalService, LegalMatter } from '../services/legal.service';

const inputCls =
  'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';
const labelCls = 'text-sm font-medium text-secondary-700 dark:text-secondary-300';

interface FormData {
  holdName: string;
  matterId: string;
  scopeDescription: string;
  holdNoticeText: string;
  dataSources: string[];
}

export default function CreateLegalHold() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillMatterId = searchParams.get('matterId') ?? '';

  const [formData, setFormData] = useState<FormData>({
    holdName: '',
    matterId: prefillMatterId,
    scopeDescription: '',
    holdNoticeText: '',
    dataSources: [],
  });
  const [dsInput, setDsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [matters, setMatters] = useState<LegalMatter[]>([]);
  const [mattersLoading, setMattersLoading] = useState(false);

  // Load matters for the dropdown
  useEffect(() => {
    let cancelled = false;
    setMattersLoading(true);
    legalService
      .getMatters({ limit: 100, status: 'ACTIVE' })
      .then((data) => { if (!cancelled) {setMatters(data.matters);} })
      .catch(() => { /* silently fail — user can type matterId */ })
      .finally(() => { if (!cancelled) {setMattersLoading(false);} });
    return () => { cancelled = true; };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addDataSource = () => {
    const ds = dsInput.trim();
    if (ds && !formData.dataSources.includes(ds)) {
      setFormData((prev) => ({ ...prev, dataSources: [...prev.dataSources, ds] }));
    }
    setDsInput('');
  };

  const removeDataSource = (ds: string) => {
    setFormData((prev) => ({ ...prev, dataSources: prev.dataSources.filter((d) => d !== ds) }));
  };

  const handleDsKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDataSource();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.holdName.trim()) {
      toast.error('Hold name is required');
      return;
    }
    if (!formData.matterId.trim()) {
      toast.error('Matter is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload: Record<string, unknown> = {
        holdName: formData.holdName.trim(),
        matterId: formData.matterId.trim(),
        dataSources: formData.dataSources,
      };

      if (formData.scopeDescription.trim()) {payload.scopeDescription = formData.scopeDescription.trim();}
      if (formData.holdNoticeText.trim()) {payload.holdNoticeText = formData.holdNoticeText.trim();}

      const newHold = await legalService.createHold(payload);
      toast.success('Legal hold created');
      navigate(`/legal/holds/${newHold.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create hold';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/legal/holds')}
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
        </button>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          New Legal Hold
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-8">
            {/* Core */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Hold Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="holdName" className={labelCls}>
                    Hold Name <span className="text-danger-500">*</span>
                  </label>
                  <Input
                    id="holdName"
                    name="holdName"
                    value={formData.holdName}
                    onChange={handleChange}
                    required
                    placeholder="Descriptive name for this hold"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="matterId" className={labelCls}>
                    Matter <span className="text-danger-500">*</span>
                  </label>
                  {matters.length > 0 ? (
                    <select
                      id="matterId"
                      name="matterId"
                      value={formData.matterId}
                      onChange={handleChange}
                      className={cn(inputCls, 'mt-1')}
                      required
                    >
                      <option value="">Select a matter...</option>
                      {matters.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.matterNumber} — {m.title}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id="matterId"
                      name="matterId"
                      value={formData.matterId}
                      onChange={handleChange}
                      required
                      placeholder={mattersLoading ? 'Loading matters...' : 'Enter matter ID'}
                      className="mt-1"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Scope */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Scope
              </h2>
              <div>
                <label htmlFor="scopeDescription" className={labelCls}>
                  Scope Description
                </label>
                <textarea
                  id="scopeDescription"
                  name="scopeDescription"
                  value={formData.scopeDescription}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Describe the scope of this legal hold..."
                  className={cn(inputCls, 'mt-1')}
                />
              </div>
            </div>

            {/* Data Sources */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Data Sources
              </h2>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.dataSources.map((ds) => (
                  <span
                    key={ds}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300 rounded-full text-sm"
                  >
                    {ds}
                    <button
                      type="button"
                      onClick={() => removeDataSource(ds)}
                      className="ml-1 hover:text-primary-600"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={dsInput}
                  onChange={(e) => setDsInput(e.target.value)}
                  onKeyDown={handleDsKeyDown}
                  placeholder="Add data source (e.g. Email, SharePoint) and press Enter"
                  className={cn(inputCls, 'flex-1')}
                />
                <Button type="button" variant="outline" onClick={addDataSource}>
                  Add
                </Button>
              </div>
            </div>

            {/* Hold Notice */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Hold Notice (Optional)
              </h2>
              <div>
                <label htmlFor="holdNoticeText" className={labelCls}>
                  Notice Text
                </label>
                <textarea
                  id="holdNoticeText"
                  name="holdNoticeText"
                  value={formData.holdNoticeText}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Enter the hold notice text that will be sent to custodians..."
                  className={cn(inputCls, 'mt-1')}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/legal/holds')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Hold'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
